import React, { useEffect, useState } from 'react';
import {
  FormControlLabel,
  Box,
  InputAdornment,
  TextField,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { TypographyLeftMenuGray, TypographyLeftMenu } from '../style/typography';
import { LEFT_MENU_PREF_SHINNKO_CITY_INDEX } from '../const/data_index';
import FilterAccordion from '../item/filter_accordion';
import { CheckboxBase } from '../style/checkbox';
import { TextFieldBase } from '../style/textfield';


export default function LeftMenusPrefShinkoCitys({ title, data = [],
  selectItems, setSelectItems,
}) {
  const [groupedData, setGroupedData] = useState([]);
  const [checked, setChecked] = useState({});
  const [expanded, setExpanded] = useState({});
  const [searchTerm, setSearchTerm] = useState('');


  // 初期表示時に全選択
  useEffect(() => {
    if (!data || data.length === 0) return;

    const selectCitys = (selectItems && selectItems.length > 0)
      ? selectItems
      : data.map(row => row[LEFT_MENU_PREF_SHINNKO_CITY_INDEX.CITY_CODE]);

    const newChecked = {};

    // 市町村チェック
    selectCitys.forEach(cityCode => {
      newChecked[cityCode] = true;
    });

    // 親の地方と県もチェックする
    data.forEach(row => {
      const cityCode = row[LEFT_MENU_PREF_SHINNKO_CITY_INDEX.CITY_CODE];
      const shinkoCode = row[LEFT_MENU_PREF_SHINNKO_CITY_INDEX.SHINKO_CODE];
      const prefCode = row[LEFT_MENU_PREF_SHINNKO_CITY_INDEX.PREF_CODE];

      if (selectCitys.includes(cityCode)) {
        newChecked[shinkoCode] = true;
        newChecked[prefCode] = true;
      }
    });

    setChecked(newChecked);

    // 初期に全市町村を選択状態にセット
    if (!selectItems || selectItems.length === 0) {
      setSelectItems(selectCitys);
    }

  }, [data]);

  useEffect(() => {
    if (groupedData.length === 0) return;

    const initialExpanded = {};
    groupedData.forEach(pref => {
      initialExpanded[pref.code] = true;  // 県を展開
    });

    setExpanded(initialExpanded);
  }, [groupedData]);

  // データを県・地方・市町村にグループ化
  useEffect(() => {
    const grouped = {};
    // データをループ
    data.forEach(row => {
      const prefCode = row[LEFT_MENU_PREF_SHINNKO_CITY_INDEX.PREF_CODE];
      const shinkoCode = row[LEFT_MENU_PREF_SHINNKO_CITY_INDEX.SHINKO_CODE];
      const cityCode = row[LEFT_MENU_PREF_SHINNKO_CITY_INDEX.CITY_CODE];
      const prefName = row[LEFT_MENU_PREF_SHINNKO_CITY_INDEX.PREF_NAME];
      const shinkoName = row[LEFT_MENU_PREF_SHINNKO_CITY_INDEX.SHINKO_NAME];
      const cityName = row[LEFT_MENU_PREF_SHINNKO_CITY_INDEX.CITY_NAME];
      // 県データ
      if (!grouped[prefCode]) {
        grouped[prefCode] = {
          code: prefCode,
          name: prefName,
          shinko: {}
        };
      }
      // 地方データ
      if (!grouped[prefCode].shinko[shinkoCode]) {
        grouped[prefCode].shinko[shinkoCode] = {
          code: shinkoCode,
          name: shinkoName,
          cities: []
        };
      }
      // 市町村データ
      grouped[prefCode].shinko[shinkoCode].cities.push({
        code: cityCode,
        name: cityName
      });
    });
    // データグループ化
    const result = Object.values(grouped).map(pref => ({
      code: pref.code,
      name: pref.name,
      shinko: Object.values(pref.shinko).map(shinko => ({
        code: shinko.code,
        name: shinko.name,
        cities: shinko.cities
      }))
    }));
    // データセット
    setGroupedData(result);
  }, [data]);

  // チェック状態を確認する関数
  const isChecked = (code) => !!checked[code];

  // チェック状態を更新する関数
  const handleCheck = (code, childrenCodes = []) => {
    const newChecked = { ...checked };
    const isNowChecked = !isChecked(code);

    // チェック状態を更新
    [code, ...childrenCodes].forEach((c) => {
      newChecked[c] = isNowChecked;
    });
    setChecked(newChecked);

    // 選択されている市町村コードだけを集める
    const selectedCityCodes = data
      .map(row => row[LEFT_MENU_PREF_SHINNKO_CITY_INDEX.CITY_CODE])
      .filter(cityCode => newChecked[cityCode]);

    // console.log(selectedCityCodes);

    setSelectItems(selectedCityCodes);
  };

  // 地方内の市町村コードを取得する関数
  const getShinkoCityCodes = (shinko) => {
    return shinko.cities.map((city) => city.code);
  };

  // 県内のすべてのコードを取得する関数
  const getPrefAllCodes = (pref) => {
    let codes = [];
    pref.shinko.forEach((s) => {
      codes.push(s.code);
      codes = codes.concat(getShinkoCityCodes(s));
    });
    return codes;
  };

  // アコーディオンの展開状態を管理する関数
  const handleAccordionChange = (panelCode) => (event, isExpanded) => {
    setExpanded(prev => ({
      ...prev,
      [panelCode]: isExpanded
    }));
  };

  // 検索条件に一致するかを判定する関数
  const matchesSearch = (cityName) => {
    return cityName.includes(searchTerm);
  };

  // フィルター処理（groupedDataのあとに追加）
  const filteredData = groupedData
    .map(pref => {
      const filteredShinko = pref.shinko
        .map(shinko => {
          const filteredCities = shinko.cities.filter(city =>
            searchTerm === '' || matchesSearch(city.name)
          );
          if (filteredCities.length === 0) return null;
          return {
            ...shinko,
            cities: filteredCities
          };
        })
        .filter(Boolean);

      if (filteredShinko.length === 0) return null;

      return {
        ...pref,
        shinko: filteredShinko
      };
    })
    .filter(Boolean);

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'column' }, pb: '1' }}>
        <Box sx={{ mb: 1 }}>
          <TypographyLeftMenuGray>地域選択</TypographyLeftMenuGray>
          {/* 検索ボックス */}
          {/* <TextFieldBase
                fullWidth
                size="small"
                placeholder="検索"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon />
                        </InputAdornment>
                    ),
                }}
            /> */}
        </Box>
        <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto' }}>
          {/* 県 */}
          {filteredData.map((pref) => (
            <FilterAccordion
              key={pref.code}
              label={pref.name}
              labelTitle={pref.name}
              checked={isChecked(pref.code)}
              onCheck={() => handleCheck(pref.code, getPrefAllCodes(pref))}
              expanded={!!expanded[pref.code]}
              onChange={handleAccordionChange(pref.code)}
            >
              {/* 地方 */}
              <Box display="flex" flexDirection="column">
                {pref.shinko.map((shinko) => (
                    <Box display="flex" flexDirection="column" paddingLeft="10px" key={shinko.code}>
                      {/* 市町村 */}
                      {shinko.cities
                        .filter((city) => searchTerm === '' || matchesSearch(city.name))
                        .map((city) => (
                          <FormControlLabel
                            key={city.code}
                            label={
                              <TypographyLeftMenu
                                noWrap
                                sx={{
                                  fontSize: { xs: '0.75rem', sm: '0.75rem', md: 'clamp(0.375rem, -0.3rem + 1.2vw, 0.75rem)' },
                                }}
                                title={city.name}
                              >
                                {city.name}
                              </TypographyLeftMenu>
                            }
                            control={
                              <CheckboxBase
                                checked={isChecked(city.code)}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => handleCheck(city.code)}
                              />
                            }
                          />
                        ))}
                    </Box>
                ))}
              </Box>
            </FilterAccordion>
          ))}
        </Box>
      </Box >
    </>
  );
};
